import { gtag, pushToDataLayer } from "@/lib/analytics";

export type ConsentCategories = {
  essential: true;
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
};

export type StoredConsent = ConsentCategories & { timestamp: number; version: number };

export const CONSENT_STORAGE_KEY = "trustscale_cookie_consent";
export const CONSENT_VERSION = 1;
const ONE_YEAR_MS = 365 * 24 * 60 * 60 * 1000;

export const CONSENT_EVENT = "trustscale:cookie-consent-open";

/** Time zones that map to the EEA / UK / Switzerland strict opt-in regime. */
const STRICT_TZ_PREFIXES = ["Europe/", "Atlantic/Reykjavik", "Atlantic/Canary", "Atlantic/Madeira", "Atlantic/Azores"];
const NON_STRICT_EUROPE_TZ = [
  "Europe/Moscow", "Europe/Kiev", "Europe/Kyiv", "Europe/Minsk", "Europe/Istanbul",
  "Europe/Belgrade", "Europe/Sarajevo", "Europe/Skopje", "Europe/Tirane", "Europe/Chisinau",
  "Europe/Podgorica", "Europe/Volgograd", "Europe/Samara", "Europe/Kaliningrad", "Europe/Simferopol",
];

/**
 * Best-effort client-side region check, mirroring the `region`-scoped
 * Consent Mode defaults in index.html. Only affects banner copy and the
 * pre-checked toggles — the authoritative gating is Google's own region
 * targeting, which uses the request IP.
 */
export function isStrictConsentRegion(): boolean {
  if (typeof Intl === "undefined") return true;
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (NON_STRICT_EUROPE_TZ.includes(tz)) return false;
    return STRICT_TZ_PREFIXES.some((p) => tz.startsWith(p));
  } catch {
    return true;
  }
}

export function getStoredConsent(): StoredConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredConsent;
    if (parsed.version !== CONSENT_VERSION) return null;
    if (Date.now() - parsed.timestamp > ONE_YEAR_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

type GtagConsentState = "granted" | "denied";

function pushConsentUpdate(consent: ConsentCategories) {
  if (typeof window === "undefined") return;
  const grant = (allowed: boolean): GtagConsentState => (allowed ? "granted" : "denied");
  // Google Consent Mode v2 update — must go through gtag() so the real
  // `arguments` object reaches the tag. A plain array push is ignored.
  gtag("consent", "update", {
    analytics_storage: grant(consent.analytics),
    ad_storage: grant(consent.marketing),
    ad_user_data: grant(consent.marketing),
    ad_personalization: grant(consent.marketing),
    functionality_storage: grant(consent.functional),
    personalization_storage: grant(consent.functional),
  });
  pushToDataLayer({
    event: "cookie_consent_update",
    cookie_consent_analytics: consent.analytics,
    cookie_consent_marketing: consent.marketing,
    cookie_consent_functional: consent.functional,
  });
}

export function saveConsent(consent: ConsentCategories) {
  const stored: StoredConsent = { ...consent, timestamp: Date.now(), version: CONSENT_VERSION };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // storage unavailable — consent applies for this session only
  }
  pushConsentUpdate(consent);
}

/** Re-apply a previously stored choice on page load. */
export function applyStoredConsent() {
  const stored = getStoredConsent();
  if (stored) pushConsentUpdate(stored);
}

/** Open the cookie preferences dialog from anywhere in the app. */
export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT));
}
